import { MutableRefObject, useEffect, useRef, useState } from 'react';

import {
  ACTIVITY_GROUPS,
  ActivityGroup,
  PROPOSAL_REVEAL_PAUSE_DURATION_IN_MS,
  PROPOSAL_REVEAL_SWEEP_DURATION_IN_MS,
} from '../OnboardingActivities.constant';
import { ActivitiesSelection } from '../OnboardingActivities.types';

interface RevealProgress {
  groups: ActivityGroup[];
  activeIndex: number;
  hasRevealedActive: boolean;
}

interface UseProposalRevealProps {
  proposedSelection: ActivitiesSelection;
  hasProposal: boolean;
  isProfileLoading: boolean;
}

export interface UseProposalRevealReturnValues {
  sectionRefs: MutableRefObject<
    Partial<Record<ActivityGroup, HTMLDivElement | null>>
  >;
  activeRevealGroup?: ActivityGroup;
  getIsGroupRevealed: (group: ActivityGroup) => boolean;
  finishReveal: () => void;
}

const REDUCED_MOTION_MEDIA_QUERY = '(prefers-reduced-motion: reduce)';

const getIsRevealComplete = (progress: RevealProgress): boolean =>
  progress.activeIndex >= progress.groups.length;

export function useProposalReveal({
  proposedSelection,
  hasProposal,
  isProfileLoading,
}: UseProposalRevealProps): UseProposalRevealReturnValues {
  const sectionRefs = useRef<
    Partial<Record<ActivityGroup, HTMLDivElement | null>>
  >({});
  const [progress, setProgress] = useState<RevealProgress | undefined>(
    undefined,
  );

  const shouldStart = hasProposal && !isProfileLoading && !progress;

  if (shouldStart) {
    const groups = ACTIVITY_GROUPS.filter(
      (group) => proposedSelection[group].length > 0,
    );
    const shouldReduceMotion =
      window.matchMedia?.(REDUCED_MOTION_MEDIA_QUERY)?.matches ?? false;

    setProgress({
      groups,
      activeIndex: shouldReduceMotion ? groups.length : 0,
      hasRevealedActive: false,
    });
  }

  useEffect(() => {
    if (!progress || getIsRevealComplete(progress)) {
      return undefined;
    }

    const { groups, activeIndex, hasRevealedActive } = progress;
    const timeoutId = setTimeout(
      () =>
        setProgress(
          hasRevealedActive
            ? { groups, activeIndex: activeIndex + 1, hasRevealedActive: false }
            : { groups, activeIndex, hasRevealedActive: true },
        ),
      hasRevealedActive
        ? PROPOSAL_REVEAL_PAUSE_DURATION_IN_MS
        : PROPOSAL_REVEAL_SWEEP_DURATION_IN_MS,
    );

    return () => clearTimeout(timeoutId);
  }, [progress]);

  const activeRevealGroup =
    progress && !getIsRevealComplete(progress) && !progress.hasRevealedActive
      ? progress.groups[progress.activeIndex]
      : undefined;

  useEffect(() => {
    if (!activeRevealGroup) {
      return;
    }

    sectionRefs.current[activeRevealGroup]?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'center',
    });
  }, [activeRevealGroup]);

  function getIsGroupRevealed(group: ActivityGroup): boolean {
    if (!progress) {
      return !hasProposal;
    }

    const groupIndex = progress.groups.indexOf(group);

    if (groupIndex === -1) {
      return true;
    }

    return (
      groupIndex < progress.activeIndex ||
      (groupIndex === progress.activeIndex && progress.hasRevealedActive)
    );
  }

  function finishReveal(): void {
    setProgress((previousProgress) => {
      if (!previousProgress || getIsRevealComplete(previousProgress)) {
        return previousProgress;
      }

      return {
        ...previousProgress,
        activeIndex: previousProgress.groups.length,
        hasRevealedActive: false,
      };
    });
  }

  return {
    sectionRefs,
    activeRevealGroup,
    getIsGroupRevealed,
    finishReveal,
  };
}
